import { Button, Chip, Input, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, Pagination, Spinner, Table, TableBody, TableCell, TableColumn, TableHeader, TableRow, type SortDescriptor } from '@nextui-org/react'
import { useSnackbar } from 'notistack'
import { FC, useCallback, useEffect, useMemo, useState, type ReactElement } from 'react'
import * as XLSX from 'xlsx'
// MdLocalShipping
import { MdFileDownload, MdOutlineSearch, MdNotificationsActive, MdDownload } from 'react-icons/md'
import { TbLayoutDashboard } from 'react-icons/tb'
import { useSelector } from 'react-redux'
import ApiStateView from '../ApiState'
// import KpiCard from '../KpiCard'
import SectionShell from '../SectionShell'
import { TONES, COURIER, TERMINO_ETIQUETA, TERMINO_ETIQUETA_LOWER } from '../../constants'
import { useLogisticBox } from '../../context/LogisticBoxContext'
import { useApi } from '../../hooks/useApi'
import { useSelectedSeller } from '../../hooks/useSelectedSeller'
import { useStoreScope } from '../../hooks/useStoreScope'
import { dashboardApi, discrepanciesApi, trackingApi, labelsApi, resolveLabelUrl, TodayStoreRow, NotificationDto, DiscrepancyEquipoDto, TrackingActiveItem } from '../../api' // LabelsSummaryKpis
import { diasTone } from '../../utils'
import { IAppStore } from '../../../../../models/store.model'
// import { ToneKey } from '../../models' // KpiCardData

const columns = [
  { name: 'Tienda', uid: 'store', align: 'start' as const },
  { name: 'Seller', uid: 'seller_name', align: 'start' as const },
  { name: 'Entregados', uid: 'entregados', align: 'center' as const, width: 70 },
  { name: 'Custodia', uid: 'en_custodia', align: 'center' as const, width: 70 },
  { name: 'En espera de recolección', uid: 'en_espera', align: 'center' as const, width: 100 },
  { name: 'Con sellos', uid: 'custodia_con_sello', align: 'center' as const, width: 75 },
  { name: 'En tránsito', uid: 'en_camino', align: 'center' as const, width: 70 },
  { name: 'Discrepancias', uid: 'discrepancias', align: 'center' as const, width: 90 },
  { name: 'Listo p/ retiro', uid: 'porcentaje_encajado', align: 'center' as const, width: 100 },
  { name: 'Días sin retiro', uid: 'dias_desde_ultimo_retiro', align: 'center' as const, width: 90 },
  { name: 'Estado', uid: 'alerta', align: 'start' as const },
  { name: '', uid: 'actions', align: 'end' as const }
]

const TONE_BADGE: Record<'green' | 'amber' | 'rosa', string> = {
  green: 'bg-[#EAF3DE] text-[#3B6D11]',
  amber: 'bg-[#FEF3C7] text-[#92400E]',
  rosa: 'bg-[#FAECE7] text-[#993C1D]'
}

type ChipColor = 'success' | 'warning' | 'danger' | 'default'
const chipColor = (tone: 'green' | 'amber' | 'rosa'): ChipColor => {
  if (tone === 'green') return 'success'
  if (tone === 'amber') return 'warning'
  if (tone === 'rosa') return 'danger'
  return 'default'
}

// Roles de jefe de tienda (ven "Notificaciones", no "Alertas").
const ROLES_JEFE_TIENDA = [
  Number(import.meta.env.VITE_PERFIL_JEFETIENDA),
  Number(import.meta.env.VITE_TI_JEFETIENDA_SELLOS)
].filter(n => Number.isFinite(n))

const DashboardView: FC = () => {
  const { goToDetail } = useLogisticBox()
  const userId = useSelector((s: IAppStore) => s.user?.id ?? null)
  const idRole = useSelector((s: IAppStore) => s.user?.id_role ?? null)
  const idStore = useSelector((s: IAppStore) => s.user?.id_store ?? null)
  const esJefeTienda = idRole !== null && ROLES_JEFE_TIENDA.includes(Number(idRole))
  const idSeller = useSelectedSeller()
  const { scopeStore } = useStoreScope()
  const { enqueueSnackbar } = useSnackbar()
  const [search, setSearch] = useState('')
  const [alertasOpen, setAlertasOpen] = useState(false)
  // Notificaciones del jefe de tienda
  const [notifOpen, setNotifOpen] = useState(false)
  const [notifs, setNotifs] = useState<NotificationDto[]>([])
  const [unread, setUnread] = useState(0)
  const [notifLoading, setNotifLoading] = useState(false)
  // Modal de discrepancias · equipos que no llegaron a bodega de una tienda.
  const [discOpen, setDiscOpen] = useState(false)
  const [discStore, setDiscStore] = useState<{ id: number, name: string } | null>(null)
  const [discLoading, setDiscLoading] = useState(false)
  const [discItems, setDiscItems] = useState<Array<DiscrepancyEquipoDto & { caja: string }>>([])
  // Modal de etiquetas activas de una tienda (para descargarlas / agregar etiqueta).
  const [labelModal, setLabelModal] = useState<{ id_store: number, store: string } | null>(null)
  // "Agregar etiqueta": checklist de equipos en la base para mover a una etiqueta nueva.
  const [baseEquipos, setBaseEquipos] = useState<Array<{ id_tradein: number, folio: string | null, equipo: string }>>([])
  const [baseLoading, setBaseLoading] = useState(false)
  const [seleccionados, setSeleccionados] = useState<Set<number>>(new Set())
  const [addingLabel, setAddingLabel] = useState(false)
  // Modal aparte para crear una etiqueta adicional (checklist de equipos).
  const [addLabelOpen, setAddLabelOpen] = useState(false)
  // const [internando, setInternando] = useState<number | null>(null)

  // Abre el modal de discrepancias y carga los equipos que no llegaron.
  // Reutiliza /discrepancies/detail filtrando los equipos con estado 'discrepancia'.
  const openDiscrepancias = async (idStore: number, store: string): Promise<void> => {
    setDiscStore({ id: idStore, name: store })
    setDiscOpen(true)
    setDiscLoading(true)
    setDiscItems([])
    try {
      const cajas = await discrepanciesApi.detailByStore(idStore, idSeller)
      const items = cajas.flatMap(c =>
        c.equipos
          .filter(e => e.estado === 'discrepancia')
          .map(e => ({ ...e, caja: c.folio_caja ?? `Caja ${c.id_collection}` }))
      )
      setDiscItems(items)
    } catch (e: any) {
      enqueueSnackbar(e?.message ?? 'No se pudieron cargar las discrepancias', { variant: 'error' })
    } finally {
      setDiscLoading(false)
    }
  }

  // const handleRetiro = async (idStore: number, tipo: 'dhl' | 'interno' = 'interno'): Promise<void> => {
  //   setInternando(idStore)
  //   try {
  //     const r = await labelsApi.generateInternalCollection(idStore, idSeller, userId === 0 ? null : userId, tipo)
  //     await Promise.all([stores.reload(), summary.reload()])
  //     const etiqueta = tipo === 'interno' ? 'Retiro interno' : 'Etiqueta DHL'
  //     const msg = r.triggered
  //       ? `${etiqueta} generada · ${r.equipos_en_custodia} equipos (folio ${r.tracking_number ?? '—'})`
  //       : (r.motivo_no_trigger ?? 'No se pudo generar la recolección')
  //     enqueueSnackbar(msg, { variant: r.triggered ? 'success' : 'warning' })
  //   } catch (e: any) {
  //     enqueueSnackbar(e?.message ?? 'No se pudo generar la recolección', { variant: 'error' })
  //   } finally {
  //     setInternando(null)
  //   }
  // }

  // const fetchSummary = useCallback(async () => await dashboardApi.labelsSummary(idSeller), [idSeller])
  const fetchToday = useCallback(async () => await dashboardApi.todayByStore(idSeller, scopeStore), [idSeller, scopeStore])

  // const summary = useApi<LabelsSummaryKpis>(fetchSummary, [idSeller])
  const stores = useApi<TodayStoreRow[]>(fetchToday, [idSeller, scopeStore])

  // Etiquetas activas (todas las de ti_box_label_current) · para el botón
  // "Descargar etiqueta" por tienda en la tabla principal.
  const fetchActiveLabels = useCallback(async () => await trackingApi.active(idSeller), [idSeller])
  const activeLabels = useApi(fetchActiveLabels, [idSeller])

  // Etiquetas descargables agrupadas por tienda. Solo cuentan las que siguen
  // ACTIVAS (etiqueta en curso): 'lista', 'en_transito' o 'atrasada'. Se excluyen
  // las 'entregada' porque su envío ya finalizó (label_current a veces las retiene).
  const labelsByStore = useMemo(() => {
    const m = new Map<number, TrackingActiveItem[]>()
    for (const it of activeLabels.data?.items ?? []) {
      if (it.estado_local === 'entregada') continue
      if (resolveLabelUrl(it.pdf_url, idSeller) === null) continue
      const arr = m.get(it.id_store) ?? []
      arr.push(it)
      m.set(it.id_store, arr)
    }
    return m
  }, [activeLabels.data, idSeller])

  // Ítems (vivos) del modal de etiquetas activas de la tienda seleccionada.
  const modalLabelItems = labelModal !== null ? (labelsByStore.get(labelModal.id_store) ?? []) : []
  // id_collection de la recolección (booking único de la tienda) · para "Agregar etiqueta".
  const modalCollectionId = modalLabelItems.find(it => it.id_collection !== null)?.id_collection ?? null

  // Al abrir el modal, carga los equipos que aún están en la etiqueta base.
  useEffect(() => {
    if (labelModal === null || modalCollectionId === null) { setBaseEquipos([]); setSeleccionados(new Set()); return }
    let cancel = false
    setBaseLoading(true)
    setSeleccionados(new Set())
    void (async () => {
      try {
        const eqs = await labelsApi.baseEquipos(modalCollectionId, idSeller)
        if (!cancel) setBaseEquipos(eqs)
      } catch {
        if (!cancel) setBaseEquipos([])
      } finally {
        if (!cancel) setBaseLoading(false)
      }
    })()
    return () => { cancel = true }
  }, [labelModal, modalCollectionId, idSeller])

  const toggleEquipo = (id: number): void => {
    setSeleccionados(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id); else next.add(id)
      return next
    })
  }

  // Crea una etiqueta nueva con los equipos marcados (se mueven de la base a la nueva).
  const handleAddLabel = async (): Promise<void> => {
    if (modalCollectionId === null) return
    if (seleccionados.size === 0) { enqueueSnackbar('Marca al menos un equipo', { variant: 'warning' }); return }
    setAddingLabel(true)
    try {
      const r = await labelsApi.addLabel(modalCollectionId, Array.from(seleccionados), idSeller, userId)
      enqueueSnackbar(`${TERMINO_ETIQUETA} creada (tracking ${r.tracking_number}) · ${r.cantidad} equipo(s)`, { variant: 'success' })
      setSeleccionados(new Set())
      setAddLabelOpen(false)
      if (modalCollectionId !== null) setBaseEquipos(await labelsApi.baseEquipos(modalCollectionId, idSeller))
      await activeLabels.reload()
    } catch (e: any) {
      enqueueSnackbar(e?.message ?? 'No se pudo crear la etiqueta', { variant: 'error' })
    } finally { setAddingLabel(false) }
  }

  const filtered = useMemo(() => {
    const rows = stores.data ?? []
    if (search === '') return rows
    const q = search.toLowerCase()
    return rows.filter(s => s.store.toLowerCase().includes(q))
  }, [stores.data, search])

  // Ordenamiento por columna (click en el encabezado).
  const [sort, setSort] = useState<SortDescriptor>({ column: '', direction: 'ascending' })
  const sorted = useMemo(() => {
    if (sort.column === undefined || sort.column === '' || sort.column === 'actions') return filtered
    const dir = sort.direction === 'descending' ? -1 : 1
    // La columna Estatus ordena por gravedad: rosa (crítico) → amber → green.
    const toneRank = (t: 'green' | 'amber' | 'rosa'): number => (t === 'rosa' ? 0 : t === 'amber' ? 1 : 2)
    const accessor = (s: TodayStoreRow): string | number | null =>
      sort.column === 'alerta' ? toneRank(s.alerta.tone) : (s as any)[sort.column as string]
    return [...filtered].sort((a, b) => {
      const av = accessor(a); const bv = accessor(b)
      if (av == null && bv == null) return 0
      if (av == null) return 1
      if (bv == null) return -1
      const cmp = typeof av === 'number' && typeof bv === 'number'
        ? av - bv
        : String(av).localeCompare(String(bv), 'es', { numeric: true, sensitivity: 'base' })
      return cmp * dir
    })
  }, [filtered, sort])

  // Paginación en cliente · sin consultas extra a la BD. Tamaño configurable.
  const PAGE_SIZES = [10, 25, 50, 100]
  const [pageSize, setPageSize] = useState(10)
  const [page, setPage] = useState(1)
  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize))
  // Si la búsqueda/orden/tamaño reduce las filas, no quedar en una página inexistente.
  useEffect(() => { setPage(1) }, [search, stores.data, sort, pageSize])
  const paged = useMemo(
    () => sorted.slice((page - 1) * pageSize, page * pageSize),
    [sorted, page, pageSize]
  )

  // Tiendas que requieren notificación = con equipos sin encajar y/o sin sello.
  const alertStores = useMemo(() => {
    const rows = stores.data ?? []
    return rows
      .filter(s => s.entregados > 0 || (s.custodia_sin_sello ?? 0) > 0 || (s.discrepancias ?? 0) > 0)
      .map(s => ({
        id_store: s.id_store,
        store: s.store,
        // Mismos chips de estatus que la columna "Estatus" de la tabla.
        alertas: s.alertas
      }))
  }, [stores.data])
  const alertCount = alertStores.length

  // ── Jefe de tienda · carga de sus notificaciones ──
  const loadNotifs = useCallback(async () => {
    if (!esJefeTienda || idStore === null || idStore <= 0) return
    setNotifLoading(true)
    try {
      const r = await dashboardApi.listNotifications(idStore, idSeller)
      setNotifs(r.items)
      setUnread(r.unread)
    } catch {
      // silencioso · el badge simplemente no se actualiza
    } finally {
      setNotifLoading(false)
    }
  }, [esJefeTienda, idStore, idSeller])

  useEffect(() => { loadNotifs() }, [loadNotifs])

  // Abre el panel de notificaciones. En el BO es 100% lectura: NO se marcan como
  // leídas ni se envía nada, solo se muestran las alertas.
  const openNotifs = (): void => {
    setNotifOpen(true)
  }

  // Descarga la tabla "Operación del día" como Excel, nombrado con la
  // fecha y hora de la extracción. Exporta lo que se ve (respeta el buscador).
  const handleDownload = (): void => {
    const rows = filtered
    if (rows.length === 0) {
      enqueueSnackbar('No hay datos para descargar', { variant: 'info' })
      return
    }

    const data = rows.map(s => ({
      Tienda: s.store,
      Seller: s.seller_name ?? '',
      RECIBIDOS: s.entregados,
      CUSTODIA: s.en_custodia,
      'En espera de recolección': s.en_espera,
      'En tránsito': s.en_camino,
      Discrepancias: s.discrepancias,
      'Porcentaje listo para retiro': s.porcentaje_encajado,
      'Días desde último retiro': s.dias_desde_ultimo_retiro ?? '',
      Estado: s.alertas.map(a => a.label).join(' · ')
    }))

    const now = new Date()
    const pad = (n: number): string => String(n).padStart(2, '0')
    const stamp = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}_${pad(now.getHours())}-${pad(now.getMinutes())}-${pad(now.getSeconds())}`

    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(data), 'Operación del día')
    XLSX.writeFile(wb, `Operacion_del_dia_${stamp}.xlsx`)
    enqueueSnackbar(`Excel descargado · ${rows.length} tiendas`, { variant: 'success' })
  }

  // En el BO las alertas son SOLO lectura: no existen acciones de "Notificar".
  const handleStoresRetry = (): void => { stores.reload() }

  // Totales de equipos en custodia (esperando) y en camino, sumados de la tabla.
  // const totalEnCustodia = useMemo(
  //   () => (stores.data ?? []).reduce((acc, s) => acc + s.en_custodia, 0),
  //   [stores.data]
  // )
  // const totalEnCamino = useMemo(
  //   () => (stores.data ?? []).reduce((acc, s) => acc + s.en_camino, 0),
  //   [stores.data]
  // )

  // const kpis: KpiCardData[] = useMemo(() => {
  //   const s = summary.data
  //   return [
  //     { value: s?.total ?? 0, label: 'Tiendas operando', tone: 'lila' as ToneKey },
  //     { value: s?.listas ?? 0, label: 'Etiquetas listas', tone: 'blue' as ToneKey },
  //     { value: s?.con_error ?? 0, label: 'Etiquetas con error', tone: 'amber' as ToneKey },
  //     { value: s?.pendientes ?? 0, label: 'Etiquetas pendientes', tone: 'green' as ToneKey },
  //     { value: totalEnCustodia, label: 'Equipos en custodia', tone: 'beige' as ToneKey },
  //     { value: totalEnCamino, label: 'Equipos en camino', tone: 'teal' as ToneKey }
  //   ]
  // }, [summary.data, totalEnCustodia, totalEnCamino])

  const renderCell = (s: TodayStoreRow, columnKey: string): ReactElement => {
    switch (columnKey) {
      case 'store':
        return <span className='font-medium text-[12px] text-neutral-800'>{s.store}</span>
      case 'seller_name':
        return <span className='text-neutral-500'>{s.seller_name ?? '—'}</span>
      case 'entregados':
        return <span className='font-semibold text-neutral-800'>{s.entregados}</span>
      case 'en_custodia':
        return <span className='font-semibold text-neutral-800'>{s.en_custodia}</span>
      case 'en_espera':
        return (s.en_espera ?? 0) > 0
          ? <span className='inline-block text-[11px] font-medium px-2 py-0.5 rounded-full bg-[#FCE7EF] text-[#B4306B]'>{s.en_espera}</span>
          : <span className='text-neutral-300'>—</span>
      case 'custodia_con_sello':
        return (s.custodia_con_sello ?? 0) > 0
          ? <span className='inline-block text-[11px] font-medium px-2 py-0.5 rounded-full bg-[#E0F2F1] text-[#0F766E]'>{s.custodia_con_sello}</span>
          : <span className='text-neutral-300'>—</span>
      case 'en_camino':
        return s.en_camino > 0
          ? <span className='inline-block text-[11px] font-medium px-2 py-0.5 rounded-full bg-[#E6F1FB] text-[#185FA5]'>{s.en_camino}</span>
          : <span className='text-neutral-300'>—</span>
      case 'discrepancias':
        return s.discrepancias > 0
          ? (
            <button
              type='button'
              title='Ver equipos que no llegaron'
              onClick={() => { openDiscrepancias(s.id_store, s.store) }}
              className='inline-block text-[11px] font-medium px-2 py-0.5 rounded-full bg-[#FAECE7] text-[#993C1D] hover:opacity-80 transition-opacity'
            >
              {s.discrepancias}
            </button>
            )
          : <span className='text-neutral-300'>—</span>
      case 'porcentaje_encajado': {
        const t = TONES[s.alerta.tone]
        const pct = Math.max(0, Math.min(100, s.porcentaje_encajado))
        return (
          <span
            className='inline-block text-[11px] font-semibold px-2.5 py-0.5 rounded-full whitespace-nowrap'
            style={{ background: t.bg, color: t.ink }}
          >
            {pct}%
          </span>
        )
      }
      case 'dias_desde_ultimo_retiro':
        return (
          <span className='font-semibold text-[12px]' style={{ color: TONES[diasTone(s.dias_desde_ultimo_retiro ?? 0)].ink }}>
            {s.dias_desde_ultimo_retiro ?? '—'}
          </span>
        )
      case 'alerta':
        return (
          <div className='flex flex-wrap gap-1'>
            {s.alertas.map((a, i) => (
              <span key={i} className={`inline-block text-[11px] font-medium px-2 py-0.5 rounded-full whitespace-nowrap ${TONE_BADGE[a.tone]}`}>
                {a.label}
              </span>
            ))}
          </div>
        )
      case 'actions':
        return (
          <div className='flex gap-2 justify-end items-center whitespace-nowrap'>
            {/* {s.en_custodia > 0 && (
              <Button
                size='sm' variant='flat'
                title='Generar retiro interno'
                isLoading={internando === s.id_store}
                onPress={() => { void handleRetiro(s.id_store) }}
                startContent={internando === s.id_store ? undefined : <MdLocalShipping className='text-[15px]' />}
                className='text-xs h-8'
              >
                Retiro interno
              </Button>
            )} */}
            {(labelsByStore.get(s.id_store)?.length ?? 0) > 0 && (
              <Button
                size='sm'
                variant='bordered'
                className='border-gray-200 text-[#151930] text-xs h-8'
                startContent={<MdDownload className='text-sm' />}
                onPress={() => { setSeleccionados(new Set()); setLabelModal({ id_store: s.id_store, store: s.store }) }}
              >
                Descargar {TERMINO_ETIQUETA_LOWER}
              </Button>
            )}
            <Button
              size='sm'
              className='bg-[#44526B] text-white text-xs h-8 min-w-[84px]'
              onPress={() => goToDetail('detalle-tienda', s.store, s.id_store)}
            >
              Revisar
            </Button>
          </div>
        )
      default:
        return <span className='text-neutral-600'>{(s as any)[columnKey]}</span>
    }
  }

  return (
    <div className='space-y-4'>
      {/* KPIs */}
      {/* <section className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'>
        <ApiStateView loading={summary.loading} error={summary.error} onRetry={summary.reload}>
          <></>
        </ApiStateView>
        {summary.data !== null && kpis.map((k, i) => <KpiCard key={i} data={k} />)}
      </section> */}

      <SectionShell
        title='Operación del día'
        icon={TbLayoutDashboard}
        noPadding
        rightSlot={
          <div className='flex items-center gap-2'>
            {esJefeTienda
              ? (
                <button
                  type='button'
                  onClick={() => { openNotifs() }}
                  className='relative inline-flex items-center gap-2 px-3 h-8 rounded-lg text-xs font-semibold border border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-50 transition-colors'
                >
                  <MdNotificationsActive className='text-sm' />
                  Notificaciones
                  {unread > 0 && (
                    <span className='absolute -top-2 -right-2 min-w-[18px] h-[18px] px-1 rounded-full bg-[#f06464] text-white text-[10px] font-bold flex items-center justify-center shadow'>
                      {unread}
                    </span>
                  )}
                </button>
                )
              : (
                <button
                  type='button'
                  onClick={() => setAlertasOpen(true)}
                  disabled={alertCount === 0}
                  className={`relative inline-flex items-center gap-2 px-3 h-8 rounded-lg text-xs font-semibold border transition-colors ${
                    alertCount > 0
                      ? 'border-red-200 bg-red-50 text-red-700 hover:bg-red-100'
                      : 'border-neutral-200 bg-white text-neutral-400 cursor-not-allowed'
                  }`}
                >
                  <MdNotificationsActive className='text-sm' />
                  Alertas
                  {alertCount > 0 && (
                    <span className='absolute -top-2 -right-2 min-w-[18px] h-[18px] px-1 rounded-full bg-[#f06464] text-white text-[10px] font-bold flex items-center justify-center shadow'>
                      {alertCount}
                    </span>
                  )}
                </button>
                )}
          </div>
        }
      >
        <div className='flex items-center justify-between px-5 pb-3 flex-wrap gap-3'>
          <div className='max-w-sm'>
            <Input
              isClearable
              size='sm'
              radius='md'
              value={search}
              onValueChange={setSearch}
              onClear={() => setSearch('')}
              classNames={{
                input: ['bg-transparent', 'text-black/90', 'placeholder:text-neutral-400', 'text-[12px]'],
                innerWrapper: 'bg-transparent',
                inputWrapper: ['bg-neutral-100', 'hover:bg-neutral-150', 'group-data-[focus=true]:bg-neutral-100', '!cursor-text', 'border-0', 'shadow-none', 'h-8']
              }}
              placeholder='Buscar por tienda…'
              startContent={<MdOutlineSearch className='text-neutral-400 pointer-events-none flex-shrink-0' />}
            />
          </div>

          <Button
            variant='bordered'
            size='sm'
            className='border-neutral-200 text-neutral-700 text-xs h-8'
            startContent={<MdFileDownload className='text-sm' />}
            isDisabled={stores.loading || filtered.length === 0}
            onPress={handleDownload}
          >
            Exportar
          </Button>
        </div>
        <div className='px-5 pb-5'>
          <ApiStateView
            loading={stores.loading}
            error={stores.error}
            onRetry={handleStoresRetry}
            empty={!stores.loading && filtered.length === 0}
            emptyText='Sin tiendas activas para mostrar'
          >
            <Table
              aria-label='Operación del día por tienda'
              sortDescriptor={sort}
              onSortChange={setSort}
              classNames={{
                wrapper: 'shadow-none rounded-none p-0 border-0',
                th: '!bg-neutral-50 !text-neutral-500 text-[11px] font-medium uppercase tracking-wider px-3 py-2 border-b border-neutral-100 h-auto rounded-none',
                td: 'px-3 py-2.5 text-[12px] border-b border-neutral-100',
                tr: 'data-[hover=true]:bg-neutral-50/60 transition-colors'
              }}
              bottomContentPlacement='outside'
              bottomContent={
                <div className='flex flex-col gap-2 pt-1'>
                  <div className='flex items-center justify-between px-1 flex-wrap gap-3'>
                    <p className='text-[12px] text-neutral-500'>
                      Mostrando <span className='font-medium text-neutral-700'>{paged.length}</span> de{' '}
                      <span className='font-medium text-neutral-700'>{filtered.length}</span> tiendas
                    </p>
                    <div className='flex items-center gap-4'>
                      <div className='flex items-center gap-3 text-xs text-neutral-500'>
                        <span className='flex items-center gap-1'><span className='w-2 h-2 rounded-full' style={{ background: TONES.green.ink }} />Al día</span>
                        <span className='flex items-center gap-1'><span className='w-2 h-2 rounded-full' style={{ background: TONES.amber.ink }} />Atención</span>
                        <span className='flex items-center gap-1'><span className='w-2 h-2 rounded-full' style={{ background: TONES.rosa.ink }} />Crítico</span>
                      </div>
                      {filtered.length > PAGE_SIZES[0] && (
                        <div className='flex items-center gap-2 text-xs text-neutral-500'>
                          <span>Filas por página</span>
                          <select
                            aria-label='Filas por página'
                            value={pageSize}
                            onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1) }}
                            className='h-7 rounded-md border border-neutral-200 bg-white px-2 text-xs text-neutral-700 focus:outline-none focus:border-neutral-400'
                          >
                            {PAGE_SIZES.map(n => <option key={n} value={n}>{n}</option>)}
                          </select>
                        </div>
                      )}
                      {totalPages > 1 && (
                        <Pagination size='sm' page={page} total={totalPages} onChange={setPage} showControls variant='light' />
                      )}
                    </div>
                  </div>
                </div>
            }
            >
              <TableHeader columns={columns}>
                {(column) => (
                  <TableColumn
                    key={column.uid}
                    align={column.align}
                    width={(column as any).width}
                    allowsSorting={column.uid !== 'actions' && column.uid !== ''}
                  >
                    {column.name}
                  </TableColumn>
                )}
              </TableHeader>
              <TableBody
                items={paged}
                isLoading={stores.loading}
                loadingContent={<Spinner label='Cargando…' />}
                emptyContent='Sin tiendas activas para mostrar'
              >
                {(s: TodayStoreRow) => (
                  <TableRow key={s.id_store}>
                    {(columnKey) => <TableCell>{renderCell(s, columnKey as string)}</TableCell>}
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </ApiStateView>
        </div>
      </SectionShell>

      {/* Modal · tiendas con alertas (solo lectura en el BO) */}
      <Modal isOpen={alertasOpen} onClose={() => setAlertasOpen(false)} size='2xl' scrollBehavior='inside'>
        <ModalContent>
          <ModalHeader className='flex flex-col gap-0'>
            <span className='text-[10px] uppercase tracking-widest text-[#37417f]/60 font-bold'>Alertas de fin de día</span>
            <span className='text-base font-bold text-[#151930]'>
              {alertCount} tienda{alertCount === 1 ? '' : 's'} con alerta{alertCount === 1 ? '' : 's'}
            </span>
          </ModalHeader>
          <ModalBody className='gap-2 pb-2'>
            {alertCount === 0
              ? <p className='text-sm text-[#151930]/50'>No hay tiendas con alertas pendientes.</p>
              : alertStores.map(a => (
                <div key={a.id_store} className='flex items-center justify-between gap-3 border border-[#eef0f7] rounded-xl px-3 py-2.5'>
                  <div className='min-w-0'>
                    <div className='text-sm font-medium text-[#151930] truncate'>{a.store}</div>
                    <div className='flex flex-wrap gap-1.5 mt-1'>
                      {a.alertas.map((al, i) => (
                        <Chip key={i} size='sm' variant='flat' color={chipColor(al.tone)}>{al.label}</Chip>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
          </ModalBody>
          <ModalFooter className='flex items-center justify-between gap-3'>
            <span className='text-xs text-[#151930]/50'>Vista de solo lectura · las alertas se gestionan desde arribos.</span>
            <Button variant='light' className='text-xs font-semibold' onPress={() => setAlertasOpen(false)}>
              Cerrar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Modal · etiquetas activas de la tienda (descargables + agregar etiqueta) */}
      <Modal isOpen={labelModal !== null} onClose={() => setLabelModal(null)} size='2xl' scrollBehavior='inside'>
        <ModalContent>
          <ModalHeader className='flex flex-col gap-0'>
            <span className='text-[10px] uppercase tracking-widest text-[#37417f]/60 font-bold'>{TERMINO_ETIQUETA}s activas</span>
            <span className='text-base font-bold text-[#151930]'>{labelModal?.store ?? 'Tienda'}</span>
          </ModalHeader>
          <ModalBody className='gap-2 pb-4'>
            {modalLabelItems.length === 0
              ? <p className='text-sm text-[#151930]/50'>No hay {TERMINO_ETIQUETA_LOWER}s activas para esta tienda.</p>
              : modalLabelItems.map(it => {
                const url = resolveLabelUrl(it.pdf_url, idSeller)
                const esReuse = it.estado_dhl?.startsWith('interno') ?? false
                return (
                  <div key={it.id_label_current} className='flex items-center justify-between gap-3 border border-[#eef0f7] rounded-xl px-3 py-2.5'>
                    <div className='min-w-0'>
                      <div className='text-sm font-medium text-[#151930] truncate '>{it.tracking_number}</div>
                      {it.referencia !== null && (
                        <div className='text-[11px] font-mono text-[#151930]/70'>Ref: {it.referencia}</div>
                      )}
                      <div className='text-[11px] font-medium text-[#151930]/80'>
                        {it.cantidad_equipos} {it.cantidad_equipos === 1 ? 'equipo' : 'equipos'} en esta caja
                      </div>
                      <div className='text-[11px] text-[#151930]/60'>
                        {it.seal !== null ? `Sellos ${it.seal}${it.seal2 !== null ? ' / ' + it.seal2 : ''}` : 'Sin sellar'}
                      </div>
                      <div className='flex flex-wrap gap-1.5 mt-1'>
                        <Chip size='sm' variant='flat' color={esReuse ? 'secondary' : 'primary'}>
                          {esReuse ? 'Retiro Reuse' : (it.courier ?? COURIER)}
                        </Chip>
                      </div>
                    </div>
                    <Button
                      as='a'
                      href={url ?? undefined}
                      target='_blank'
                      rel='noreferrer'
                      size='sm'
                      isDisabled={url === null}
                      className='bg-black text-white text-xs h-8 shrink-0'
                      startContent={<MdDownload className='text-sm' />}
                    >
                      Descargar
                    </Button>
                  </div>
                )
              })}
            {/* Botón para abrir el modal de crear una etiqueta adicional. */}
            {modalCollectionId !== null && (
              <div className='mt-2'>
                <Button
                  size='sm'
                  className='bg-[#151930] text-white text-xs font-semibold'
                  startContent={<span className='text-sm leading-none'>+</span>}
                  onPress={() => { setSeleccionados(new Set()); setAddLabelOpen(true) }}
                >
                  Agregar {TERMINO_ETIQUETA_LOWER}
                </Button>
              </div>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Modal · crear una etiqueta adicional eligiendo los equipos (checklist) */}
      <Modal isOpen={addLabelOpen} onClose={() => setAddLabelOpen(false)} size='lg' scrollBehavior='inside'>
        <ModalContent>
          <ModalHeader className='flex flex-col gap-0'>
            <span className='text-[10px] uppercase tracking-widest text-[#37417f]/60 font-bold'>Agregar {TERMINO_ETIQUETA}</span>
            <span className='text-base font-bold text-[#151930]'>{labelModal?.store ?? 'Tienda'}</span>
          </ModalHeader>
          <ModalBody className='gap-2 pb-2'>
            <p className='text-xs text-[#151930]/60'>Selecciona los equipos que irán en esta {TERMINO_ETIQUETA_LOWER}. Se moverán de la etiqueta base a la nueva.</p>
            {baseLoading
              ? <p className='text-xs text-[#151930]/50'>Cargando equipos…</p>
              : baseEquipos.length === 0
                ? <p className='text-xs text-[#151930]/50'>No hay equipos disponibles en la etiqueta base.</p>
                : (
                  <div className='flex flex-col gap-1'>
                    {baseEquipos.map(e => (
                      <label key={e.id_tradein} className='flex items-center gap-2 text-xs text-[#151930] rounded-lg px-2 py-1.5 border border-[#eef0f7] hover:bg-neutral-50 cursor-pointer'>
                        <input
                          type='checkbox'
                          checked={seleccionados.has(e.id_tradein)}
                          onChange={() => toggleEquipo(e.id_tradein)}
                        />
                        <span className='font-medium'>{e.equipo}</span>
                        {e.folio !== null && <span className='text-[11px] text-[#151930]/50'>· folio {e.folio}</span>}
                      </label>
                    ))}
                  </div>
                  )}
          </ModalBody>
          <ModalFooter className='flex items-center justify-between gap-2'>
            <span className='text-[11px] text-[#151930]/50'>{seleccionados.size} seleccionado(s)</span>
            <div className='flex gap-2'>
              <Button variant='bordered' className='border-gray-200 text-[#151930]' onPress={() => setAddLabelOpen(false)}>Cancelar</Button>
              <Button
                className='bg-[#151930] text-white font-semibold'
                isLoading={addingLabel}
                isDisabled={seleccionados.size === 0}
                onPress={() => { void handleAddLabel() }}
              >
                Agregar {TERMINO_ETIQUETA_LOWER}
              </Button>
            </div>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Modal · equipos que no llegaron (discrepancias) */}
      <Modal isOpen={discOpen} onClose={() => setDiscOpen(false)} size='3xl' scrollBehavior='inside'>
        <ModalContent>
          <ModalHeader className='flex flex-col gap-0'>
            <span className='text-[10px] uppercase tracking-widest text-[#37417f]/60 font-bold'>Discrepancias</span>
            <span className='text-base font-bold text-[#151930]'>
              {discStore?.name ?? 'Tienda'} · equipos que no llegaron
            </span>
          </ModalHeader>
          <ModalBody className='gap-2 pb-4'>
            {discLoading
              ? <div className='flex justify-center py-6'><Spinner label='Cargando…' /></div>
              : discItems.length === 0
                ? <p className='text-sm text-[#151930]/50'>No hay equipos pendientes de llegar.</p>
                : (
                  <Table
                    aria-label='Equipos que no llegaron' removeWrapper isStriped
                    classNames={{
                      th: 'bg-[#eceef6] text-[#151930] text-[11px] font-bold uppercase tracking-wide py-2 h-9',
                      td: 'py-2 text-xs'
                    }}
                  >
                    <TableHeader>
                      <TableColumn>Id Tradein</TableColumn>
                      <TableColumn>Equipo</TableColumn>
                      <TableColumn>Folio</TableColumn>
                      <TableColumn>Sellos</TableColumn>
                      <TableColumn>Caja</TableColumn>
                    </TableHeader>
                    <TableBody items={discItems}>
                      {(e) => (
                        <TableRow key={`${e.caja}-${e.id_tradein}`}>
                          <TableCell><span className=' text-xs'>{e.id_tradein}</span></TableCell>
                          <TableCell><span className='font-medium text-xs text-[#151930]'>{e.equipo}</span></TableCell>
                          <TableCell><span className=' text-xs'>{e.folio ?? '—'}</span></TableCell>
                          <TableCell><span className=' text-xs'>{[e.seal, e.seal2].filter(Boolean).join(' · ') || '—'}</span></TableCell>
                          <TableCell><span className=' text-xs'>{e.caja}</span></TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                  )}
          </ModalBody>
          <ModalFooter>
            <span className='text-xs text-[#151930]/50'>
              {discItems.length > 0 && `${discItems.length} equipo${discItems.length === 1 ? '' : 's'} sin llegar a bodega`}
            </span>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Modal · notificaciones del jefe de tienda */}
      <Modal isOpen={notifOpen} onClose={() => setNotifOpen(false)} size='2xl' scrollBehavior='inside'>
        <ModalContent>
          <ModalHeader className='flex flex-col gap-0'>
            <span className='text-[10px] uppercase tracking-widest text-[#37417f]/60 font-bold'>Notificaciones</span>
            <span className='text-base font-bold text-[#151930]'>Mensajes del administrador</span>
          </ModalHeader>
          <ModalBody className='gap-2 pb-6'>
            {notifLoading && notifs.length === 0
              ? <div className='flex justify-center py-6'><Spinner label='Cargando…' /></div>
              : notifs.length === 0
                ? <p className='text-sm text-[#151930]/50'>No tienes notificaciones.</p>
                : notifs.map(n => (
                  <div
                    key={n.id}
                    className={`rounded-xl border px-3 py-2.5 ${n.leida ? 'border-[#eef0f7] bg-white' : 'border-[#cfd6f3] bg-[#f3f5fe]'}`}
                  >
                    <div className='flex items-start gap-2'>
                      <MdNotificationsActive className='text-base mt-0.5 shrink-0' style={{ color: n.tipo === 'alerta' ? '#f06464' : '#37417f' }} />
                      <div className='min-w-0 flex-1'>
                        <div className='flex items-center justify-between gap-2'>
                          <span className='text-sm font-semibold text-[#151930]'>{n.titulo ?? 'Notificación'}</span>
                          <span className='text-[11px] text-[#151930]/45 shrink-0'>{new Date(n.fecha).toLocaleString('es-CL', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                        </div>
                        <div className='text-xs text-[#151930]/70 mt-0.5'>{n.mensaje}</div>
                      </div>
                    </div>
                  </div>
                ))}
          </ModalBody>
          <ModalFooter>
            <Button variant='flat' className='text-xs' isLoading={notifLoading} onPress={() => { loadNotifs() }}>
              Actualizar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  )
}

export default DashboardView
